import { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    CircularProgress,
    Chip,
} from '@mui/material';
import { BarChart } from '@mui/icons-material';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { GewichtApi } from '../../../services/api';
import Notification from '../../util/notifications/Notification';
import {
    BALKEN_BREITE,
    MIN_BREITE,
    berechneWochenDifferenzen,
    buildChartData,
    buildChartOptions,
    datalabelPlugin,
} from './gewichtwochenverlauf/wochenVerlaufUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function GewichtWochenVerlauf({ nutzerId }) {
    const [loading, setLoading] = useState(true);
    const [verlauf, setVerlauf] = useState([]);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (nutzerId) fetchVerlauf();
    }, [nutzerId]);

    const fetchVerlauf = async () => {
        try {
            setLoading(true);
            const response = await GewichtApi.getErweiterteStats(nutzerId);
            const rawVerlauf = response.data?.gewichtsverlauf || [];
            setVerlauf(berechneWochenDifferenzen(rawVerlauf));
        } catch (err) {
            console.error('Fehler beim Laden des Wochenverlaufs:', err);
            setMessage({ type: 'error', text: 'Fehler beim Laden des Wochenverlaufs' });
        } finally {
            setLoading(false);
        }
    };

    const gesamtVeraenderung = verlauf.reduce((sum, w) => sum + w.diff, 0);
    const positivWochen = verlauf.filter((w) => w.diff > 0).length;
    const negativWochen = verlauf.filter((w) => w.diff < 0).length;

    const innerWidth = Math.max(MIN_BREITE, verlauf.length * BALKEN_BREITE);

    const maxAbs = verlauf.length
        ? Math.ceil(Math.max(...verlauf.map((w) => Math.abs(w.diff))) + 0.3)
        : 2;

    const chartData = buildChartData(verlauf);
    const chartOptions = buildChartOptions(maxAbs);

    if (loading) {
        return (
            <Card
                sx={{
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    minHeight: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress />
            </Card>
        );
    }

    if (!verlauf.length) return null;

    return (
        <Box sx={{ mb: 3 }}>
            {message.type === 'error' && (
                <Notification
                    type={message.type}
                    message={message.text}
                    onClose={() => setMessage({ type: '', text: '' })}
                />
            )}

            <Card
                sx={{
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Header */}
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <BarChart sx={{ color: '#3b82f6', fontSize: 32 }} />
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{
                                color: '#e0f2fe',
                                fontSize: { xs: '1.1rem', sm: '1.5rem' },
                            }}
                        >
                            Wöchentliche Veränderung
                        </Typography>
                    </Box>

                    {/* Zusammenfassung */}
                    <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
                        <Chip
                            label={`Gesamt: ${gesamtVeraenderung >= 0 ? '+' : ''}${gesamtVeraenderung.toFixed(2)} kg`}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(59, 130, 246, 0.15)',
                                color: '#93c5fd',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                            }}
                        />
                        <Chip
                            label={`↑ ${positivWochen} Wochen`}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(34, 197, 94, 0.12)',
                                color: '#22c55e',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                            }}
                        />
                        <Chip
                            label={`↓ ${negativWochen} Wochen`}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(239, 68, 68, 0.12)',
                                color: '#ef4444',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                            }}
                        />
                    </Box>

                    {/* Scrollbarer Chart-Container */}
                    <Box
                        sx={{
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            '&::-webkit-scrollbar': { height: 4 },
                            '&::-webkit-scrollbar-track': {
                                bgcolor: 'rgba(255,255,255,0.05)',
                                borderRadius: 2,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                bgcolor: 'rgba(59, 130, 246, 0.4)',
                                borderRadius: 2,
                            },
                        }}
                    >
                        {/* Feste Pixelbreite → Chart.js bekommt stabile Maße */}
                        <Box sx={{ width: innerWidth, height: 300 }}>
                            <Bar
                                data={chartData}
                                options={chartOptions}
                                plugins={[datalabelPlugin]}
                                width={innerWidth}
                                height={300}
                            />
                        </Box>
                    </Box>

                    {verlauf.length > 5 && (
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                textAlign: 'center',
                                color: '#475569',
                                mt: 1,
                                fontSize: '0.7rem',
                            }}
                        >
                            ← scroll →
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

export default GewichtWochenVerlauf;
